<template>
    <Teleport to="body">
        <Transition name="floater">
            <div
                v-if="visible && current"
                class="sga-floater"
                @mouseenter="pause"
                @mouseleave="resume"
            >
                <span v-if="current.isPrivate" class="sga-floater-badge">Exclusive</span>
                <button class="sga-floater-close" @click="dismiss" aria-label="Dismiss">&times;</button>
                <img
                    src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png"
                    alt="Star"
                    class="sga-floater-icon"
                />
                <Link v-if="isInertiaEvent" :href="current.url" class="sga-floater-label" @click="trackClick">{{ current.label }}</Link>
                <a v-else :href="current.url" class="sga-floater-label" @click="trackClick">{{ current.label }}</a>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed} from 'vue'
import {usePage, router, Link} from '@inertiajs/vue3'

const page = usePage()
const current = ref(null)
const visible = ref(false)
let hideTimer = null
let removeNavigateListener = null
let hasTrackedDisplay = false

const isInertiaEvent = computed(() => [
    'create_pitch',
    'share_pitch',
    'vote_pitch',
    'sponsor_audition',
    'give_stars_audition',
    'share_audition',
    'comment_audition',
].includes(current.value?.goalType))

function getCookie(name) {
    const match = document.cookie.match('(?:^|;)\\s*' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    return match ? decodeURIComponent(match[1]) : null
}

function getDismissed() {
    try { return JSON.parse(localStorage.getItem('sga_dismissed') || '[]') } catch { return [] }
}

function saveDismissed(ids) {
    try { localStorage.setItem('sga_dismissed', JSON.stringify(ids)) } catch {}
}

function track(event, campaignId, goalType) {
    if (typeof LTIAnalytics !== 'undefined') {
        LTIAnalytics.track(event, {
            campaign_id: campaignId,
            goal_type: goalType
        })
    }
}

function trackClick() {
    if (current.value) {
        track('Campaign Floater Clicked', current.value.campaignId, current.value.goalType)
    }
}

function pickItem() {
    const items = page.props.sgaFloaterItems ?? []
    const dismissed = getDismissed()
    const candidates = items.filter(item => {
        if (item.recurringPeriod) {
            return getCookie('sga_rec_' + item.campaignId) !== item.recurringPeriod
        }
        return !dismissed.includes(item.campaignId) && !getCookie('sga_done_' + item.campaignId)
    })
    if (!candidates.length) return null
    const privateCandidates = candidates.filter(i => i.isPrivate)
    const pool = privateCandidates.length ? privateCandidates : candidates
    return pool[Math.floor(Math.random() * pool.length)]
}

function show() {
    clearTimeout(hideTimer)
    visible.value = false
    const item = pickItem()
    if (!item) return

    // Check if this is a new item or the same item being reshown
    const isNewItem = !current.value || current.value.campaignId !== item.campaignId

    current.value = item
    visible.value = true

    // Only track if this is a new item and hasn't been tracked yet
    if (isNewItem && !hasTrackedDisplay) {
        track('Campaign Floater Displayed', item.campaignId, item.goalType)
        hasTrackedDisplay = true
    } else if (isNewItem) {
        // Reset flag for new items on subsequent navigations
        hasTrackedDisplay = false
        track('Campaign Floater Displayed', item.campaignId, item.goalType)
        hasTrackedDisplay = true
    }

    scheduleHide()
}

function scheduleHide() {
    hideTimer = setTimeout(() => { visible.value = false }, 5000)
}

function pause() {
    clearTimeout(hideTimer)
    hideTimer = null
}

function resume() {
    scheduleHide()
}

function dismiss() {
    if (current.value) {
        track('Campaign Floater Dismissed', current.value.campaignId, current.value.goalType)
    }
    clearTimeout(hideTimer)
    visible.value = false
    if (current.value && !current.value.recurringPeriod) {
        const dismissed = getDismissed()
        if (!dismissed.includes(current.value.campaignId)) {
            saveDismissed([...dismissed, current.value.campaignId])
        }
    }
}

onMounted(() => {
    show()
    removeNavigateListener = router.on('navigate', () => show())
})

onUnmounted(() => {
    clearTimeout(hideTimer)
    if (removeNavigateListener) removeNavigateListener()
})
</script>

<style scoped>
.sga-floater {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9998;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #1a1a1a;
    border: 1px solid #f5a623;
    border-radius: 12px;
    padding: 12px 16px;
    padding-right: 28px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    font-family: sans-serif;
    max-width: 420px;
}

.sga-floater-badge {
    position: absolute;
    top: -10px;
    left: 12px;
    background: #7c3aed;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    pointer-events: none;
}

.sga-floater-close {
    position: absolute;
    top: 6px;
    right: 8px;
    background: none;
    border: none;
    color: #888;
    font-size: 15px;
    cursor: pointer;
    line-height: 1;
    padding: 0;
}

.sga-floater-close:hover {
    color: #ccc;
}

.sga-floater-icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
}

.sga-floater-label {
    color: #f7c26b;
    font-weight: 700;
    font-size: 13px;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.sga-floater-label:hover {
    color: #ffd989;
}

.floater-enter-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.floater-leave-active {
    transition: opacity 0.4s ease;
}
.floater-enter-from {
    opacity: 0;
    transform: translateY(8px);
}
.floater-leave-to {
    opacity: 0;
}
</style>
